import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { Toggle, ToggleGroup } from '@ps/web-shared-components';
import { required } from '@ps/web-shared-validators';
import classNames from 'clsx';
import React, { Fragment } from 'react';
import { useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';

import { EulaType } from '@swf/core/enums';
import useFeature from '@swf/core/hooks/useFeature';
import { select } from '@swf/core/store';

import FirstCheckbox from './FirstCheckbox/FirstCheckbox';
import styles from './RegulatoryCheckboxes.scss';
import SelectAllToggle from './SelectAllToggle';
import useLabel from './hooks/useLabel';

const additionalEulaTypes: Record<Props['variant'], { [Key in `${Sites}`]?: [`${EulaType}`] }> = {
  stars: {
    [Sites.ITA]: [EulaType.GamingAccAgreement],
    [Sites.BUL]: [EulaType.GameRulesConditions],
    [Sites.IND]: [EulaType.IndianStatesRestrictions]
  },
  first: {
    [Sites.ITA]: [EulaType.GamingAccAgreement],
    [Sites.BUL]: [EulaType.GameRulesConditions],
    [Sites.IND]: [EulaType.IndianStatesRestrictions],
    [Sites.CA_ON]: [EulaType.IAmEligibleToPlay]
  },
  rmf: {}
};

export const getEulaTypes = (
  variant: Props['variant'],
  siteId: Sites,
  separatedEulaPrivacyPolicy: boolean
): `${EulaType}`[] => {
  const result = separatedEulaPrivacyPolicy
    ? [EulaType.Eula, EulaType.Privacy]
    : [EulaType.CombinedEulaPrivacy];
  return [...result, ...(additionalEulaTypes[variant][siteId] || [])];
};

const getRmfContent = (siteId: Sites) => {
  switch (true) {
    case siteId === Sites.POR:
      return (
        <Fragment key={EulaType.PortugalEligibleToPlay}>
          <FirstCheckbox eulaType={EulaType.PortugalEligibleToPlay} variant="rmf" isRequired />
        </Fragment>
      );
    default:
      return (
        <>
          <Fragment key={EulaType.PlayerIsNot3Party}>
            <FirstCheckbox
              eulaType={EulaType.PlayerIsNot3Party}
              variant="rmf"
              isRequired={siteId === Sites.USMI}
            />
          </Fragment>
          <Fragment key={EulaType.InformationIsComplete}>
            <FirstCheckbox eulaType={EulaType.InformationIsComplete} variant="rmf" isRequired />
          </Fragment>
        </>
      );
  }
};

export interface Props {
  variant: 'first' | 'rmf' | 'stars';
  onEulaLinkClick?: () => void;
  onPrivacyLinkClick?: () => void;
  isSelectAllToggleVisible?: boolean;
}

const getCheckboxes = (
  variant: Props['variant'],
  eulaTypes: `${EulaType}`[],
  siteId: Sites
): string[] => {
  switch (variant) {
    case 'first':
      return eulaTypes;
    case 'stars':
      return [...eulaTypes, EulaType.MarketingMailList];
    case 'rmf':
      return siteId === Sites.POR
        ? [EulaType.PortugalEligibleToPlay]
        : [EulaType.PlayerIsNot3Party, EulaType.InformationIsComplete];
    default:
      return [];
  }
};

const RegulatoryCheckboxes: React.FC<Props> = ({
  variant,
  onEulaLinkClick,
  onPrivacyLinkClick,
  isSelectAllToggleVisible = false
}) => {
  const siteId = useSelector(select.cms.getSiteId);
  const separatedEulaPrivacyPolicy = useFeature(FeatureFlag.SeparatedEulaPrivacyPolicy);
  const getLabel = useLabel(onEulaLinkClick, onPrivacyLinkClick);
  const eulaTypes = getEulaTypes(variant, siteId, separatedEulaPrivacyPolicy);
  const checkboxes = getCheckboxes(variant, eulaTypes, siteId);

  const { values } = useFormState();

  return (
    <>
      {isSelectAllToggleVisible && <SelectAllToggle checkboxes={checkboxes} values={values} />}

      {variant === 'first' && (
        <>
          {eulaTypes.map((eulaType: `${EulaType}`) => (
            <Fragment key={eulaType}>
              <FirstCheckbox
                eulaType={eulaType as EulaType}
                onEulaLinkClick={onEulaLinkClick}
                onPrivacyLinkClick={onPrivacyLinkClick}
              />
            </Fragment>
          ))}
        </>
      )}
      {variant === 'rmf' && getRmfContent(siteId)}
      {variant === 'stars' && (
        <ToggleGroup id="checkboxes" label="">
          <Toggle
            type="checkbox"
            id={EulaType.MarketingMailList}
            name={EulaType.MarketingMailList}
            label={getLabel('stars', EulaType.MarketingMailList)}
            className={styles.label}
          />
          {eulaTypes.map((eulaType: `${EulaType}`) => (
            <Toggle
              id={eulaType}
              key={eulaType}
              name={eulaType}
              type="checkbox"
              validators={[required()]}
              label={getLabel('stars', eulaType as EulaType)}
              className={classNames({
                [styles.label]: [EulaType.Eula, EulaType.IndianStatesRestrictions].includes(
                  eulaType as EulaType
                )
              })}
            />
          ))}
        </ToggleGroup>
      )}
    </>
  );
};

export default RegulatoryCheckboxes;
